"use client";

import Link from "next/link";
import type { KeyboardEvent, RefObject } from "react";
import { useCallback, useEffect } from "react";
import { FaCircleInfo } from "react-icons/fa6";
import { X } from "lucide-react";

import { menuSections } from "@/components/layout/navigationData";
import { getTourChromeStyles } from "@/components/layout/tourChromeStyles";
import type { TourPageThemeKey } from "@/components/sections/tour-page/types";
import useScrollToTop from "@/lib/hooks/useScrollToTop";
import { cn } from "@/lib/utils/cn";
import MenuSection from "./MenuSection";

export default function MobileMenu({
  isGhostPage = false,
  tourTheme,
  isOpen = false,
  handleClose,
  dialogRef,
  handleDialogClose,
}: {
  isGhostPage?: boolean;
  tourTheme?: TourPageThemeKey;
  isOpen: boolean;
  handleClose: () => void;
  dialogRef?: RefObject<HTMLDialogElement | null>;
  handleDialogClose: () => void;
}) {
  useScrollToTop(isOpen);
  const chrome = getTourChromeStyles(tourTheme, isGhostPage).header;

  const getFocusableMenuElements = useCallback(
    (dialogElement: HTMLDialogElement) => Array.from(
      dialogElement.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => {
      const style = window.getComputedStyle(element);

      return (
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        element.getAttribute("aria-hidden") !== "true"
      );
    }),
    [],
  );

  const moveFocusWithinDialog = useCallback(
    (dialogElement: HTMLDialogElement, moveBackward: boolean) => {
      const focusableElements = getFocusableMenuElements(dialogElement);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement || !lastElement) return;

      const activeElement = document.activeElement;
      const activeIndex =
        activeElement instanceof HTMLElement
          ? focusableElements.indexOf(activeElement)
          : -1;

      if (activeIndex === -1) {
        (moveBackward ? lastElement : firstElement).focus();
        return;
      }

      const nextIndex = moveBackward
        ? activeIndex <= 0
          ? focusableElements.length - 1
          : activeIndex - 1
        : activeIndex >= focusableElements.length - 1
          ? 0
          : activeIndex + 1;

      focusableElements[nextIndex]?.focus();
    },
    [getFocusableMenuElements],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === "Tab") {
      if (event.defaultPrevented) return;

      event.preventDefault();
      moveFocusWithinDialog(event.currentTarget, event.shiftKey);
    }
  };

  useEffect(() => {
    const dialogElement = dialogRef?.current;
    if (!dialogElement) return;

    if (isOpen) {
      if (!dialogElement.open) {
        dialogElement.showModal();
      }

      const handleDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
        if (event.key !== "Tab" || !dialogElement.open) return;

        event.preventDefault();
        moveFocusWithinDialog(dialogElement, event.shiftKey);
      };

      document.addEventListener("keydown", handleDocumentKeyDown, true);
      requestAnimationFrame(() => {
        getFocusableMenuElements(dialogElement)[0]?.focus();
      });
      return () => {
        document.removeEventListener("keydown", handleDocumentKeyDown, true);
      };
    }

    if (dialogElement.open) {
      dialogElement.close();
    }
  }, [dialogRef, getFocusableMenuElements, isOpen, moveFocusWithinDialog]);

  return (
    <dialog
      ref={dialogRef}
      id="mobile-sliding-menu"
      aria-label="Mobile navigation"
      onClose={handleDialogClose}
      onCancel={handleClose}
      onKeyDown={handleKeyDown}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
      className="fixed inset-0 z-[60] m-0 h-screen max-h-none w-screen max-w-none border-0 bg-transparent p-0 text-white backdrop:bg-transparent xl:hidden"
    >
      <aside
        data-mobile-menu-panel
        className={`${chrome.mobileMenuPanel} ml-auto flex h-[100dvh] w-[320px] flex-col overflow-y-auto overscroll-none p-5 px-3 pb-20 text-white shadow-xl`}
      >
        <div className="flex justify-end px-2 pb-2">
          <button
            type="button"
            aria-label="Close mobile navigation"
            onClick={handleClose}
            className={cn(
              "inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border transition",
              chrome.mobileMenuCloseButton,
            )}
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
        <nav aria-label="Mobile navigation">
          {menuSections.map((section) => (
            <MenuSection
              key={section.key}
              section={section}
              handleClose={handleClose}
              accentColor={chrome.mobileMenuOpenAccent}
              textColor={chrome.mobileMenuOpenText}
              linkClassName={chrome.mobileMenuLink}
            />
          ))}

          <Link
            href="/about-us"
            onClick={handleClose}
            className={cn(
              "flex w-full min-w-full items-center border-b px-2 py-6 text-left font-semibold uppercase",
              chrome.mobileMenuLink,
            )}
          >
            <span className="flex items-center gap-3">
              <FaCircleInfo
                size={23}
                className={cn("transition-all", chrome.mobileMenuAboutIcon)}
              />
              <span className="font-semibold">About Us</span>
            </span>
          </Link>
        </nav>
      </aside>
    </dialog>
  );
}
